import React from 'react';

import { DEFAULT_BOARD_COLOR } from '@aline/common';
import { link } from '@aline/routes';
import { Dialog, IconButton, Typography } from '@mui/material';
import { Formik } from 'formik';
import { useNavigate } from 'react-router-dom';
import { AlineInput } from 'src/AlineInput';
import { AlineButton, AlineTextarea, LucideIconButton, Spacer, ToggleSwitch } from 'src/components';
import { CloseIcon } from 'src/components/icons/CloseIcon';
import { useCreateBoard } from 'src/hooks';
import { makeStyles } from 'tss-react/mui';
import * as Yup from 'yup';

interface CreateBoardFormValues {
  color: string;
  description: string;
  icon: string;
  name: string;
  useSigningColumns: boolean;
}

const CreateBoardSchema = Yup.object().shape({
  color: Yup.string().required().default(DEFAULT_BOARD_COLOR),
  description: Yup.string().default(''),
  icon: Yup.string().required().default('LayoutDashboard'),
  name: Yup.string().trim().required('Board name is required'),
  useSigningColumns: Yup.boolean().required().default(true),
});

const useStyles = makeStyles()(theme => ({
  actions: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  borderNone: {
    '&&': {
      border: 'none',
    },
    '&&:focus': {
      border: 'none',
      outline: 'none',
    },
  },
  closeButton: {
    color: theme.palette.text.secondary,
  },
  closeIcon: {
    height: 16,
    width: 16,
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2.5),
    padding: theme.spacing(3),
  },
  header: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'space-between',
  },
  inputBase: {
    '&::placeholder': {
      fontSize: theme.typography.pxToRem(24),
      fontWeight: 700,
    },
    fontSize: theme.typography.pxToRem(24),
    fontWeight: 700,
    padding: theme.spacing(0.5, 0),
  },
  paper: {
    borderRadius: 16,
    width: 900,
  },
  titleContainer: {
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'row',
    flexGrow: 1,
    gap: theme.spacing(1),
  },
  titleIcon: {
    cursor: 'pointer',
    height: 36,
    // The box is sized to the icon exactly, so it must opt out of the shared button padding.
    padding: 0,
    width: 36,
  },
  titleInputBorder: {
    '&&': {
      border: 'none',
      minHeight: 'unset',
      padding: 0,
    },
    '&&:focus-within': {
      border: 'none',
    },
    flex: 1,
  },
  toggleContainer: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(1),
    justifyContent: 'space-between',
  },
  toggleLabel: {
    color: theme.palette.text.primary,
    fontSize: theme.typography.pxToRem(14),
  },
}));

interface CreateBoardDialogProps {
  onClose: () => void;
  open: boolean;
}

export function CreateBoardDialog({ onClose, open }: CreateBoardDialogProps): JSX.Element {
  const { classes } = useStyles();
  const navigate = useNavigate();
  const { createBoard, loading } = useCreateBoard();

  const initialValues: CreateBoardFormValues = {
    color: DEFAULT_BOARD_COLOR,
    description: '',
    icon: 'LayoutDashboard',
    name: '',
    useSigningColumns: true,
  };

  return (
    <Formik<CreateBoardFormValues>
      enableReinitialize
      initialValues={initialValues}
      validationSchema={CreateBoardSchema}
      onSubmit={async (values, { resetForm }) => {
        const newBoard = await createBoard({
          color: values.color,
          description: values.description.trim() || null,
          icon: values.icon,
          name: values.name.trim(),
          useSigningColumns: values.useSigningColumns,
        });

        if (newBoard) {
          navigate(link.WorkflowBoardSettings({ boardId: newBoard.id }));
          resetForm();
          onClose();
        }
      }}
    >
      {({ handleSubmit, isValid, resetForm, setFieldValue, values }) => {
        return (
          <Dialog
            classes={{ paper: classes.paper }}
            maxWidth="lg"
            open={open}
            onClose={() => {
              resetForm();
              onClose();
            }}
          >
            <form className={classes.form} onSubmit={handleSubmit}>
              <header className={classes.header}>
                <div className={classes.titleContainer}>
                  <LucideIconButton
                    className={classes.titleIcon}
                    selectedColor={values.color}
                    size={36}
                    value={values.icon}
                    onChange={icon => setFieldValue('icon', icon)}
                    onColorChange={color => setFieldValue('color', color)}
                  />
                  <AlineInput
                    classes={{ input: classes.inputBase }}
                    fullWidth={false}
                    inputClassName={classes.titleInputBorder}
                    placeholder="Board Title"
                    value={values.name}
                    onChange={e => setFieldValue('name', e.target.value)}
                  />
                </div>

                <IconButton
                  className={classes.closeButton}
                  size="small"
                  onClick={() => {
                    resetForm();
                    onClose();
                  }}
                >
                  <CloseIcon className={classes.closeIcon} />
                </IconButton>
              </header>

              <div className={classes.content}>
                <AlineTextarea
                  className={classes.borderNone}
                  maxRows={5}
                  minRows={3}
                  placeholder="Add description..."
                  value={values.description}
                  onChange={e => setFieldValue('description', e.target.value)}
                />
              </div>

              <div className={classes.actions}>
                <div className={classes.toggleContainer}>
                  <Typography className={classes.toggleLabel}>
                    Use signing status columns?
                  </Typography>
                  <ToggleSwitch
                    toggle={() => setFieldValue('useSigningColumns', !values.useSigningColumns)}
                    toggled={values.useSigningColumns}
                  />
                </div>
                <Spacer />
                <AlineButton
                  color="primary"
                  disabled={!isValid || loading}
                  loading={loading}
                  type="submit"
                  variant="contained"
                >
                  Create
                </AlineButton>
              </div>
            </form>
          </Dialog>
        );
      }}
    </Formik>
  );
}
